import { Outlet, createRootRouteWithContext, Link, useNavigate, useLocation } from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { LogOut, Loader2 } from 'lucide-react';
import { meOptions } from '../lib/resources/queries';
import { useLogout } from '../lib/resources/auth';
import { Button } from '../components/ui/button';

interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
});

function RootLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const meQuery = useQuery(meOptions());
  const logout = useLogout();
  const isAdmin = meQuery.data?.role === 'ADMIN';
  const isLoginRoute = location.pathname === '/login';

  // Login page is its own surface — no nav.
  if (isLoginRoute) return <Outlet />;

  return (
    <div className="min-h-screen flex flex-col">
      <header className="h-14 border-b border-border bg-surface flex items-center px-8 gap-6">
        <Link to="/" className="font-bold text-foreground text-base">
          SuSaInvoicer
        </Link>
        {isAdmin && (
          <span className="text-[11px] font-bold uppercase tracking-wide bg-foreground text-background px-2 py-0.5 rounded-[4px]">
            Admin
          </span>
        )}
        <nav className="ml-2 flex gap-5 text-[13px] text-foreground">
          <Link to="/" className="hover:text-primary [&.active]:text-primary [&.active]:font-medium">
            Dashboard
          </Link>
          {isAdmin && (
            <>
              <Link to="/admin" className="hover:text-primary [&.active]:text-primary [&.active]:font-medium">
                Overview
              </Link>
              <Link to="/admin/users" className="hover:text-primary [&.active]:text-primary [&.active]:font-medium">
                Users
              </Link>
              <Link to="/admin/fees" className="hover:text-primary [&.active]:text-primary [&.active]:font-medium">
                Fees
              </Link>
              <Link to="/admin/companies" className="hover:text-primary [&.active]:text-primary [&.active]:font-medium">
                Companies
              </Link>
              <Link to="/admin/audit-log" className="hover:text-primary [&.active]:text-primary [&.active]:font-medium">
                Audit log
              </Link>
            </>
          )}
          <Link to="/themes" className="hover:text-primary [&.active]:text-primary [&.active]:font-medium ml-auto">
            Themes
          </Link>
        </nav>
        <div className="ml-auto flex items-center gap-3">
          {meQuery.data && (
            <>
              <span className="text-[12px] text-foreground-muted" title={meQuery.data.email}>
                {meQuery.data.legalName}
              </span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => logout.mutate(undefined, { onSuccess: () => navigate({ to: '/login' }) })}
                disabled={logout.isPending}
                title="Sign out"
              >
                {logout.isPending ? <Loader2 className="size-3.5 animate-spin" /> : <LogOut className="size-3.5" />}
              </Button>
            </>
          )}
        </div>
      </header>
      <main className="flex-1">
        <Outlet />
      </main>
    </div>
  );
}
